import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, SlidersHorizontal } from "lucide-react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DishCard } from "@/components/Cards";
import { CUISINES } from "@/lib/mockData";
import { fetchDishes } from "@/lib/apiData";

const CUISINE_NAMES = ["All", ...CUISINES.map((c) => c.name)];
const SORTS = [
  { id: "popular", label: "Most loved" },
  { id: "price-low", label: "Price: low" },
  { id: "price-high", label: "Price: high" },
];

export default function Marketplace() {
  const [params] = useSearchParams();
  const [query, setQuery] = useState("");
  const [cuisine, setCuisine] = useState("All");
  const [sort, setSort] = useState("popular");
  const { data: DISHES = [] } = useQuery({ queryKey: ["dishes"], queryFn: () => fetchDishes() });

  useEffect(() => {
    const q = params.get("q");
    const c = params.get("cuisine");
    if (q) setQuery(q);
    if (c && CUISINE_NAMES.includes(c)) setCuisine(c);
  }, [params]);

  const dishes = useMemo(() => {
    let list = [...DISHES];
    if (cuisine !== "All") list = list.filter((d) => d.cuisine === cuisine);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((d) => d.name.toLowerCase().includes(q) || d.cuisine.toLowerCase().includes(q) || d.tags.join(" ").toLowerCase().includes(q));
    }
    if (sort === "price-low") list.sort((a, b) => a.price - b.price);
    if (sort === "price-high") list.sort((a, b) => b.price - a.price);
    if (sort === "popular") list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [query, cuisine, sort, DISHES]);

  return (
    <div data-testid="marketplace-page" className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <p className="text-xs uppercase tracking-[0.25em] text-[#d00000] mb-3">The marketplace</p>
        <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter text-neutral-900">Every dish, one table.</h1>
        <p className="mt-4 text-neutral-600 max-w-xl">Browse authentic dishes from verified indie kitchens around the world.</p>
      </motion.div>

      {/* Controls */}
      <div className="mt-10 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
        <div className="relative max-w-md w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search dishes…"
            data-testid="marketplace-search"
            className="h-12 pl-12 bg-white border-neutral-300 text-neutral-900 rounded-none focus-visible:ring-[#d00000]"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SlidersHorizontal className="w-4 h-4 text-neutral-400" />
          {SORTS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSort(s.id)}
              data-testid={`sort-${s.id}`}
              className={`text-sm px-3 py-2 border transition-colors ${sort === s.id ? "border-[#d00000] text-[#d00000] bg-[#d00000]/5" : "border-neutral-300 text-neutral-500 hover:text-neutral-900"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Cuisine filters */}
      <div className="mt-6 flex gap-2 flex-wrap border-b border-neutral-200 pb-6">
        {CUISINE_NAMES.map((c) => (
          <button
            key={c}
            onClick={() => setCuisine(c)}
            data-testid={`filter-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
            className={`text-sm px-4 py-2 rounded-none transition-colors ${cuisine === c ? "bg-[#d00000] text-white" : "bg-white border border-neutral-300 text-neutral-700 hover:border-[#d00000]"}`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Results */}
      <p className="mt-6 text-sm text-neutral-400" data-testid="result-count">{dishes.length} dishes</p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {dishes.map((d, i) => <DishCard key={d.id} dish={d} index={i} />)}
      </div>
      {dishes.length === 0 && (
        <div className="py-24 text-center" data-testid="no-results">
          <p className="font-display text-2xl text-neutral-900">Nothing matches that craving.</p>
          <p className="text-neutral-500 mt-2">Try a different search or cuisine.</p>
          <Button onClick={() => { setQuery(""); setCuisine("All"); }} className="mt-6 bg-[#d00000] hover:bg-[#a30000] rounded-none">Reset filters</Button>
        </div>
      )}
    </div>
  );
}
